import React, { useCallback, useState } from 'react'
import type { ToggleSwitchProps } from './ToggleSwitch.types'
import './ToggleSwitch.css'

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  defaultChecked = false,
  disabled = false,
  className = '',
  id,
  'aria-label': ariaLabel = 'Toggle',
  darkMode: _darkMode = false,
  previewState,
  onChange,
}) => {
  const [internalChecked, setInternalChecked] = useState(defaultChecked)
  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isChecked = checked ?? internalChecked

  const handleClick = useCallback(() => {
    if (isPreview || isDisabled) {
      return
    }

    const nextChecked = !isChecked

    if (checked === undefined) {
      setInternalChecked(nextChecked)
    }

    onChange?.(nextChecked)
  }, [checked, isChecked, isDisabled, isPreview, onChange])

  const classNames = [
    'ds-toggle-switch',
    isChecked ? 'ds-toggle-switch--checked' : 'ds-toggle-switch--unchecked',
    isDisabled ? 'ds-toggle-switch--disabled' : '',
    isPreview ? 'ds-toggle-switch--preview' : '',
    previewState ? `ds-toggle-switch--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      id={id}
      role="switch"
      className={classNames}
      aria-checked={isChecked}
      aria-label={ariaLabel}
      disabled={isDisabled}
      tabIndex={isPreview ? -1 : undefined}
      onClick={handleClick}
    >
      <span className="ds-toggle-switch__thumb" aria-hidden="true" />
    </button>
  )
}

export default ToggleSwitch
